/**
 * The Cleaners list and a cleaner's page (design/prototype/Cleaners.dc.html,
 * CleanerDetail.dc.html): who works where, their regular week, what is coming
 * up, and how to reach them. A day's total counts 15 minutes between each
 * extra location that day (docs/majik/AUTOMATION-LOGIC.md → generate_schedule).
 * Pure.
 */
import type { BusinessRules } from '../business-rules/rules.ts'
import { hourText } from '../business-rules/rules.ts'
import type { Location } from '../clients/client.ts'
import {
  type Contact,
  contactName,
  initialsOf,
  languageLabels,
  phoneText,
} from '../contacts/contact.ts'
import type { Job, RecurringJob, WorkType } from '../jobs/job.ts'
import { type ComingUpEntry, comingUp, projectedVisits } from '../jobs/visits.ts'
import { weeklyServiceOf } from '../jobs/weekly-service.ts'
import { type WeekdayKey, weekdayKeys } from '../shared/cadence.ts'
import { addDays, clockAt, clockOf, dayKey, weekdayDay } from '../shared/zoned.ts'
import { languageMix } from './broadcast.ts'
import { type Assignment, type Cleaner, hoursText, type Shift, shiftText } from './cleaner.ts'
import { type ScheduleDocument, scheduleDocument } from './schedule.ts'
import { awayNow, type TimeOff, type TimeOffEntry, timeOffEntry } from './time-off.ts'

/** The break counted between two locations on the same day. */
export const BREAK_BETWEEN_LOCATIONS = 0.25

const DAY_LABELS: Record<WeekdayKey, string> = {
  mon: 'Mon',
  tue: 'Tue',
  wed: 'Wed',
  thu: 'Thu',
  fri: 'Fri',
  sat: 'Sat',
  sun: 'Sun',
}

export interface WeekRow {
  locationId: string
  locationName: string
  clientId: string
  startTime: string | null
  /** "9:30 AM" — when they start there. */
  startText: string | null
  hours: number[]
  total: number
}

/** A cleaner's regular week: a row per location, and each day's total with breaks. */
export function regularWeek(
  assignments: readonly Assignment[],
  locations: ReadonlyMap<string, Location>,
): { rows: WeekRow[]; dayTotals: number[]; total: number; days: string[] } {
  const rows = assignments
    .flatMap((a) => {
      const location = locations.get(a.locationId)
      if (!location) return []
      const hours = weekdayKeys.map((k) => a.hours[k])
      return [
        {
          locationId: location.id,
          locationName: location.name,
          clientId: location.clientId,
          startTime: a.startTime,
          startText: a.startTime ? clockOf(a.startTime) : null,
          hours,
          total: hours.reduce((s, h) => s + h, 0),
        },
      ]
    })
    .sort((a, b) => (a.startTime ?? '99').localeCompare(b.startTime ?? '99'))
  const dayTotals = weekdayKeys.map((_, i) => {
    const worked = rows.map((r) => r.hours[i] ?? 0).filter((h) => h > 0)
    if (worked.length === 0) return 0
    return worked.reduce((s, h) => s + h, 0) + BREAK_BETWEEN_LOCATIONS * (worked.length - 1)
  })
  return {
    rows,
    dayTotals,
    total: dayTotals.reduce((s, h) => s + h, 0),
    days: weekdayKeys.map((k) => DAY_LABELS[k]),
  }
}

export interface CleanerRow {
  id: string
  name: string
  initials: string
  status: Cleaner['status']
  shift: Shift | null
  shiftLabel: string
  language: string
  phone: string
  /** Has a mobile, wants job texts and never asked for no texts: a broadcast reaches them. */
  textable: boolean
  weeklyHours: number
  /** "Cooper Plaza Wellness, Robert Wellinger", or "Fill-in". */
  worksAt: string
  /** The same places one by one, for a list that shows the first and counts the rest. */
  places: string[]
  /** Their regular locations. */
  locationIds: string[]
  fillIn: boolean
  /** "Today · 8:00 AM", "Thu, Oct 22 · 9:00 AM", or "Nothing booked". */
  next: string
}

export interface CleanersContext {
  rules: BusinessRules
  now: string
  contacts: ReadonlyMap<string, Contact>
  /** The recurring jobs these cleaners are on — their regular week comes from them (D-034). */
  recurring: readonly RecurringJob[]
  locations: ReadonlyMap<string, Location>
  /** Jobs not deleted, with any of these cleaners on them. */
  jobs: readonly Job[]
  /** These cleaners' time off, removed periods left out. */
  timeOff: readonly TimeOff[]
  /**
   * Every job their recurring jobs have made for a visit in the next
   * `COMING_UP_DAYS`, whoever is on it now and wherever it moved — so a visit
   * already made into a job is never also projected (D-034). A cleaner's page only.
   */
  ruleJobs?: readonly Job[]
}

/** How far a cleaner's Coming up projects their recurring visits. */
export const COMING_UP_DAYS = 28

/** A cleaner's regular week, from the recurring jobs they are on. */
export function weekOf(cleanerId: string, ctx: CleanersContext) {
  const tz = ctx.rules.timeZone
  return regularWeek(
    weeklyServiceOf(ctx.recurring, dayKey(ctx.now, tz), tz).filter(
      (a) => a.cleanerId === cleanerId,
    ),
    ctx.locations,
  )
}

export function nextJob(cleanerId: string, ctx: CleanersContext): Job | undefined {
  return ctx.jobs
    .filter(
      (j) =>
        j.status === 'scheduled' &&
        j.scheduledAt >= ctx.now &&
        j.cleaners.some((c) => c.cleanerId === cleanerId),
    )
    .sort((a, b) => a.scheduledAt.localeCompare(b.scheduledAt))[0]
}

/** "Today", "Tomorrow", or "Oct 29" — short enough for a number card. */
function relativeDay(at: string, ctx: Pick<CleanersContext, 'rules' | 'now'>): string {
  const tz = ctx.rules.timeZone
  const day = dayKey(at, tz)
  if (day === dayKey(ctx.now, tz)) return 'Today'
  if (day === dayKey(new Date(Date.parse(ctx.now) + 86_400_000).toISOString(), tz))
    return 'Tomorrow'
  return weekdayDay(at, tz).replace(/^\w+, /, '')
}

export function whenText(
  at: string,
  ctx: { rules: Pick<BusinessRules, 'timeZone'>; now: string },
): string {
  const tz = ctx.rules.timeZone
  const today = dayKey(ctx.now, tz) === dayKey(at, tz)
  return `${today ? 'Today' : weekdayDay(at, tz)} · ${clockAt(at, tz)}`
}

export function cleanerRow(cleaner: Cleaner, ctx: CleanersContext): CleanerRow {
  const contact = ctx.contacts.get(cleaner.contactId)
  const name = contact ? contactName(contact) : 'Unknown cleaner'
  const week = weekOf(cleaner.id, ctx)
  const next = nextJob(cleaner.id, ctx)
  const places = [...new Set(week.rows.map((r) => r.locationName))]
  return {
    id: cleaner.id,
    name,
    initials: initialsOf(name),
    status: cleaner.status,
    shift: cleaner.shift,
    shiftLabel: shiftText(cleaner.shift),
    language: contact ? languageLabels[contact.language] : 'English',
    phone: phoneText(contact?.phone ?? null),
    textable: Boolean(contact?.phone) && !contact?.textsBlocked && cleaner.notifyByText,
    weeklyHours: week.total,
    worksAt: places.length ? places.join(', ') : 'Fill-in',
    places,
    locationIds: [...new Set(week.rows.map((r) => r.locationId))],
    fillIn: week.rows.length === 0,
    next: next ? whenText(next.scheduledAt, ctx) : 'Nothing booked',
  }
}

export function cleanerStats(rows: readonly CleanerRow[]) {
  const active = rows.filter((r) => r.status === 'active')
  const languages = languageMix(active)
  const regular = active.filter((r) => !r.fillIn)
  const places = new Set(active.flatMap((r) => r.locationIds)).size
  return [
    {
      label: 'Active cleaners',
      value: String(active.length),
      note: `${active.filter((r) => r.shift === 'daytime').length} daytime · ${active.filter((r) => r.shift === 'nighttime').length} nighttime`,
    },
    {
      label: 'Regular hours a week',
      value: hoursText(active.reduce((s, r) => s + r.weeklyHours, 0)),
      note: `across ${places} ${places === 1 ? 'location' : 'locations'}`,
    },
    {
      label: 'On a regular location',
      value: String(regular.length),
      note: `${active.length - regular.length} are fill-ins`,
      info: 'A fill-in has no regular location yet. They still get one-off jobs and shift texts.',
    },
    {
      label: 'Languages for texts',
      value: String(languages.length),
      note: languages.map((l) => `${l.language} ${l.count}`).join(' · '),
    },
  ]
}

export interface CleanerDetail extends CleanerRow {
  email: string
  notifyByText: boolean
  contactId: string
  languageCode: Contact['language']
  textsBlocked: boolean
  week: ReturnType<typeof regularWeek>
  stats: { label: string; value: string; note: string }[]
  /** Their next jobs and projected visits (D-034). */
  comingUp: ComingUpEntry[]
  jobs: {
    jobId: string
    when: string
    name: string
    sub: string
    hours: string
    status: Job['status']
  }[]
  scheduleText: string
  /** What the schedule PDF holds. */
  schedule: ScheduleDocument
  /** When the daily schedule text goes out. */
  dailyAt: string
  /** Time off going on or still ahead, soonest first; past periods are kept, not listed. */
  timeOff: TimeOffEntry[]
  /** "Away until Nov 3" while a period is on, else null. */
  awayUntil: string | null
  /** Today on the business's calendar, 'YYYY-MM-DD' — the form's first day. */
  today: string
}

export function cleanerDetail(
  cleaner: Cleaner,
  ctx: CleanersContext & {
    workTypes: ReadonlyMap<string, WorkType>
    clientNames: ReadonlyMap<string, string>
  },
): CleanerDetail {
  const row = cleanerRow(cleaner, ctx)
  const contact = ctx.contacts.get(cleaner.contactId)
  const tz = ctx.rules.timeZone
  const week = weekOf(cleaner.id, ctx)
  const mine = ctx.jobs
    .filter((j) => j.cleaners.some((c) => c.cleanerId === cleaner.id) && j.status !== 'cancelled')
    .sort((a, b) => b.scheduledAt.localeCompare(a.scheduledAt))
  const upcoming = mine
    .filter((j) => j.status === 'scheduled' && j.scheduledAt >= ctx.now)
    .reverse()
  const since = new Date(Date.parse(ctx.now) - 30 * 86_400_000).toISOString()
  const recent = mine.filter((j) => j.scheduledAt >= since && j.scheduledAt < ctx.now)
  const next = upcoming[0]
  const today = dayKey(ctx.now, tz)
  // The schedule text's hour is said once, in Details → Daily schedule (D-035).
  const when = (at: string, hours: number | null) =>
    `${clockAt(at, tz)}${hours ? ` · ${hoursText(hours)}` : ''}`
  const label = (job: Job) => {
    const location = ctx.locations.get(job.locationId)
    const client = location ? ctx.clientNames.get(location.clientId) : undefined
    const work = ctx.workTypes.get(job.workTypeIds[0] ?? '')?.name ?? 'Visit'
    return { location: location?.name ?? job.name, sub: [client, work].filter(Boolean).join(' · ') }
  }
  const mineOff = ctx.timeOff.filter((p) => p.cleanerId === cleaner.id)
  const places = [...new Set(week.rows.map((r) => r.locationName))]
  const listText = week.rows
    .map((r) => {
      const days = week.days.filter((_, i) => (r.hours[i] ?? 0) > 0)
      return `${r.locationName}: ${days.join(', ')}`
    })
    .join('; ')
  return {
    ...row,
    email: contact?.email ?? '',
    notifyByText: cleaner.notifyByText,
    contactId: cleaner.contactId,
    languageCode: contact?.language ?? 'english',
    textsBlocked: contact?.textsBlocked ?? false,
    week,
    stats: [
      {
        label: 'Regular hours',
        value: hoursText(week.total),
        note: week.rows.length ? 'a week' : 'Fill-in',
      },
      {
        label: 'Locations',
        value: String(places.length),
        note: places.length ? places.slice(0, 2).join(', ') : 'None yet',
      },
      {
        label: 'Next job',
        value: next ? relativeDay(next.scheduledAt, ctx) : '—',
        note: next
          ? `${weekdayDay(next.scheduledAt, tz)} · ${clockAt(next.scheduledAt, tz)} · ${label(next).location}`
          : 'Nothing booked',
      },
      {
        label: 'Jobs, last 30 days',
        value: String(recent.length),
        note: `${hoursText(recent.reduce((s, j) => s + (j.hours ?? 0), 0))} worked`,
      },
    ],
    comingUp: comingUp(
      [
        ...upcoming.map((job) => ({
          at: job.scheduledAt,
          entry: {
            key: job.id,
            kind: 'job' as const,
            jobId: job.id,
            dayKey: dayKey(job.scheduledAt, tz),
            title: label(job).location,
            sub: when(job.scheduledAt, job.hours),
            state: null,
            cover: null,
          },
        })),
        ...projectedVisits(
          ctx.recurring.filter((r) => r.cleaners.some((c) => c.cleanerId === cleaner.id)),
          [...ctx.jobs, ...(ctx.ruleJobs ?? [])],
          { from: today, to: addDays(today, COMING_UP_DAYS), now: ctx.now, timeZone: tz },
        ).map((visit) => ({
          at: visit.at,
          entry: {
            key: `${visit.ruleId}|${visit.day}`,
            kind: 'visit' as const,
            jobId: null,
            dayKey: visit.day,
            title: ctx.locations.get(visit.locationId)?.name ?? visit.name,
            sub: when(visit.at, visit.hours),
            state: visit.state,
            cover: null,
          },
        })),
      ],
      4,
    ),
    jobs: mine.slice(0, 50).map((job) => ({
      jobId: job.id,
      when: `${weekdayDay(job.scheduledAt, tz)} · ${clockAt(job.scheduledAt, tz)}`,
      name: label(job).location,
      sub: label(job).sub,
      hours: job.hours ? hoursText(job.hours) : '—',
      status: job.status,
    })),
    scheduleText: week.rows.length
      ? `Hi ${contact?.firstName ?? ''}, here is your regular schedule: ${listText}. ${hoursText(week.total)} a week.`
      : `Hi ${contact?.firstName ?? ''}, you have no regular locations right now. We will text you one-off jobs.`,
    schedule: scheduleDocument(week, ctx.locations),
    dailyAt: hourText(ctx.rules.cleanerListHour),
    timeOff: mineOff
      .map((p) => timeOffEntry(p, ctx.now, tz))
      .filter((e) => e.status !== 'past')
      .sort((a, b) => a.startDate.localeCompare(b.startDate)),
    awayUntil: awayNow(mineOff, ctx.now, tz),
    today: dayKey(ctx.now, tz),
  }
}
